// ============================================================
// cf-auth.js � JWT Token + User Context Management
// Loaded on every page via _Layout.cshtml (after jquery, site.js).
//
// Storage strategy:
//   accessToken  ? sessionStorage (cf_at) � survives page navigation,
//                  cleared when tab closes (sessionStorage lifecycle)
//   refreshToken ? sessionStorage (cleared when tab closes)
//   cf_me        ? sessionStorage (cached MeResponse JSON � avoids
//                  /api/v1/auth/me on every page load / F5)
//   cf_session   ? session cookie (read by AuthGuardMiddleware on server)
//                  Value is always "1" � not a token, just a presence flag.
//
// Avatar caching strategy:
//   avatarFileId  ? from MeResponse (Guid of FileAttachment)
//   avatarDataUrl ? fetched once via /api/v1/files/{id}/thumbnail, converted
//                   to base64 Data URL, cached in cf_me sessionStorage.
//                   Subsequent page navigations use cached data URL � zero
//                   additional API calls. Invalidated on logout / invalidateMe().
//
// Public API:
//   CF.auth.setTokens(access, refresh, expiresIn)
//   CF.auth.redirectAfterLogin(access, refresh, expiresIn, redirectTo)
//   CF.auth.getAccessToken()
//   CF.auth.isAuthenticated()
//   CF.auth.logout()
//   CF.auth.api(jqueryAjaxOptions)
//   CF.auth.loadMe(forceRefresh)  ? populate CF.user from cache or API
//   CF.auth.invalidateMe(reload)  ? clear cache; reload=true re-fetches
//
// CF.user � populated by loadMe():
//   CF.user.userId, username, email, fullName, avatarFileId, avatarDataUrl
//   CF.user.roles[], permissions[], menus[]
//   CF.user.hasPermission(code), hasRole(role), isLoaded()
// ============================================================

window.CF = window.CF || {};

// ??????????????????????????????????????????????????????????
// CF.user � client-side user context store
// Populated by CF.auth.loadMe() from cache or /api/v1/auth/me
// ??????????????????????????????????????????????????????????
CF.user = {
    userId:        null,
    username:      '',
    email:         '',
    fullName:      '',
    avatarFileId:  null,
    avatarDataUrl: null,
    roles:         [],
    permissions:   [],
    menus:         [],

    hasPermission: function (code) {
        return this.permissions.indexOf(code) !== -1;
    },
    hasRole: function (role) {
        return this.roles.indexOf(role) !== -1;
    },
    isLoaded: function () {
        return !!this.userId || !!this.username;
    },

    _clear: function () {
        this.userId        = null;
        this.username      = '';
        this.email         = '';
        this.fullName      = '';
        this.avatarFileId  = null;
        this.avatarDataUrl = null;
        this.roles         = [];
        this.permissions   = [];
        this.menus         = [];
    },

    _load: function (me) {
        this.userId        = me.userId || null;
        this.username      = me.username;
        this.email         = me.email || '';
        this.fullName      = me.fullName;
        this.avatarFileId  = me.avatarFileId || null;
        this.avatarDataUrl = me.avatarDataUrl || null;
        this.roles         = me.roles || [];
        this.permissions   = me.permissions || [];
        this.menus         = me.menus || [];
    }
};

CF.auth = (function () {
    'use strict';

    // � In-memory store �
    var _accessToken  = null;
    var _expiresAt    = null;   // ms timestamp when access token expires
    var _isRefreshing = false;
    var _refreshQueue = [];     // pending $.Deferred waiting for refresh to complete

    // � Pending XHR tracker � aborted on page unload to prevent
    //    server-side OperationCanceledException during navigation �
    var _pendingXhrs = new Set();
    var _isUnloading = false;

    // � Storage keys for token persistence across page navigations.
    //    sessionStorage survives same-tab navigation but is cleared
    //    when the tab closes � same lifecycle as refreshToken. �
    var _ACCESS_TOKEN_KEY = 'cf_at';
    var _EXPIRES_AT_KEY   = 'cf_at_exp';

    window.addEventListener('beforeunload', function () {
        _isUnloading = true;
        _pendingXhrs.forEach(function (xhr) {
            if (xhr && typeof xhr.abort === 'function') {
                xhr.abort();
            }
        });
        _pendingXhrs.clear();
    });

    // Lazy getter � read API_BASE_URL at call time, not at parse time.
    function _apiBase() {
        return (typeof API_BASE_URL !== 'undefined' && API_BASE_URL)
            ? API_BASE_URL
            : '';
    }

    var LOGIN_PATH      = '/login';
    var REFRESH_PATH    = '/api/v1/auth/refresh-token';
    var ME_PATH         = '/api/v1/auth/me';
    var ME_CACHE_KEY    = 'cf_me';
    var REFRESH_BUFFER  = 60 * 1000; // start proactive refresh 60s before expiry
    var SESSION_COOKIE  = 'cf_session';

    // ??????????????????????????????????????
    // COOKIE HELPERS
    // ??????????????????????????????????????
    function _setSessionCookie() {
        var isSecure = window.location.protocol === 'https:';
        var cookie   = SESSION_COOKIE + '=1; path=/; SameSite=Strict';
        if (isSecure) cookie += '; Secure';
        document.cookie = cookie;
    }

    function _clearSessionCookie() {
        var isSecure = window.location.protocol === 'https:';
        var cookie   = SESSION_COOKIE + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Strict';
        if (isSecure) cookie += '; Secure';
        document.cookie = cookie;
    }

    function _hasSessionCookie() {
        return document.cookie.split(';').some(function (c) {
            return c.trim().startsWith(SESSION_COOKIE + '=');
        });
    }

    // ??????????????????????????????????????
    // TOKEN PERSISTENCE HELPERS (sessionStorage)
    // Survives page navigation within the same tab.
    // Cleared when tab closes (sessionStorage lifecycle).
    // ??????????????????????????????????????
    function _persistTokens() {
        try {
            if (_accessToken && _expiresAt) {
                sessionStorage.setItem(_ACCESS_TOKEN_KEY, _accessToken);
                sessionStorage.setItem(_EXPIRES_AT_KEY, String(_expiresAt));
            }
        } catch (e) {
            // sessionStorage full or unavailable � silently ignore
        }
    }

    function _restoreTokens() {
        try {
            var token = sessionStorage.getItem(_ACCESS_TOKEN_KEY);
            var exp   = sessionStorage.getItem(_EXPIRES_AT_KEY);
            if (token && exp) {
                var expiresAt = parseInt(exp, 10);
                if (expiresAt > Date.now()) {
                    _accessToken = token;
                    _expiresAt   = expiresAt;
                    return true;
                }
                _clearPersistedTokens();
            }
        } catch (e) {
            // ignore
        }
        return false;
    }

    function _clearPersistedTokens() {
        sessionStorage.removeItem(_ACCESS_TOKEN_KEY);
        sessionStorage.removeItem(_EXPIRES_AT_KEY);
    }

    // ??????????????????????????????????????
    // ME CACHE HELPERS (sessionStorage)
    // ??????????????????????????????????????
    function _getMeCache() {
        try {
            var raw = sessionStorage.getItem(ME_CACHE_KEY);
            if (!raw) return null;
            return JSON.parse(raw);
        } catch (e) {
            sessionStorage.removeItem(ME_CACHE_KEY);
            return null;
        }
    }

    function _setMeCache(meData) {
        try {
            sessionStorage.setItem(ME_CACHE_KEY, JSON.stringify(_sanitizeMeForCache(meData)));
        } catch (e) {
            // sessionStorage full or unavailable � silently ignore
        }
    }

    function _updateMeCacheAvatar(dataUrl) {
        try {
            var cached = _getMeCache();
            if (cached) {
                cached.avatarDataUrl = dataUrl;
                sessionStorage.setItem(ME_CACHE_KEY, JSON.stringify(cached));
            }
        } catch (e) {
            // silently ignore
        }
    }

    // ??????????????????????????????????????
    // INTERNAL: strip sensitive / unnecessary data before caching
    // ??????????????????????????????????????
    function _sanitizeMeForCache(me) {
        return {
            username:       me.username,
            fullName:       me.fullName,
            email:          me.email || '',
            avatarFileId:   me.avatarFileId || null,
            avatarDataUrl:  me.avatarDataUrl || null,
            roles:          me.roles || [],
            // Multi-company (script 093): dibutuhkan switcher header pada
            // navigasi ber-cache — tanpa ini tombol hilang di halaman kedua.
            companies:       me.companies || [],
            activeCompanyId: me.activeCompanyId || null,
            permissions:    me.permissions || [],
            menus:          _stripMenuIds(me.menus || []),
            localization:   me.localization,
            pagination:     me.pagination
        };
    }

    function _stripMenuIds(menus) {
        if (!menus || !menus.length) return [];
        return menus.map(function (m) {
            var clean = {
                name:       m.name,
                code:       m.code,
                url:        m.url,
                icon:       m.icon,
                iconColor:  m.iconColor,
                orderIndex: m.orderIndex,
                menuLevel:  m.menuLevel
            };
            if (m.children && m.children.length > 0) {
                clean.children = _stripMenuIds(m.children);
            }
            return clean;
        });
    }

    function _clearMeCache() {
        sessionStorage.removeItem(ME_CACHE_KEY);
    }

    // ??????????????????????????????????????
    // PUBLIC: setTokens
    // Called after login or after a successful refresh.
    // accessToken persisted in sessionStorage + memory;
    // refreshToken goes to sessionStorage.
    // cf_session cookie is written so server-side guard allows access.
    // ??????????????????????????????????????
    function setTokens(accessToken, refreshToken, expiresIn) {
        _accessToken = accessToken;
        _expiresAt   = Date.now() + (parseInt(expiresIn, 10) * 1000);

        _persistTokens();
        sessionStorage.setItem('refreshToken', refreshToken);
        _setSessionCookie();

        // Remove any legacy keys written by old login.js
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('expiresIn');
    }

    // ??????????????????????????????????????
    // PUBLIC: redirectAfterLogin
    // Sets tokens, clears stale me cache (force fresh after login),
    // then verifies session cookie before navigating.
    // ??????????????????????????????????????
    function redirectAfterLogin(accessToken, refreshToken, expiresIn, redirectTo) {
        setTokens(accessToken, refreshToken, expiresIn);
        _clearMeCache(); // force fresh /me call on landing page

        // Verify the cookie actually landed before navigating.
        // Poll up to 20 � 50ms = 1000ms, then redirect regardless.
        var attempts = 0;
        var MAX      = 20;

        function doRedirect() {
            window.location.href = redirectTo || '/';
        }

        function checkAndRedirect() {
            if (_hasSessionCookie() || attempts >= MAX) {
                doRedirect();
                return;
            }
            attempts++;
            setTimeout(checkAndRedirect, 50);
        }

        checkAndRedirect();
    }

    // ??????????????????????????????????????
    // PUBLIC: getAccessToken � raw value, use CF.auth.api() instead
    // ??????????????????????????????????????
    function getAccessToken() {
        return _accessToken;
    }

    // ??????????????????????????????????????
    // PUBLIC: isAuthenticated
    // ??????????????????????????????????????
    function isAuthenticated() {
        return !!_accessToken || !!sessionStorage.getItem('refreshToken');
    }

    // ??????????????????????????????????????
    // INTERNAL: proactive expiry check
    // ??????????????????????????????????????
    function _isTokenExpiringSoon() {
        if (!_expiresAt) return true;
        return Date.now() >= (_expiresAt - REFRESH_BUFFER);
    }

    // ??????????????????????????????????????
    // INTERNAL: refreshTokens
    // Returns a jQuery promise that resolves when new tokens are ready.
    // Concurrent callers are queued � only one refresh request is made.
    // ??????????????????????????????????????
    function _refreshTokens() {
        var storedRefresh = sessionStorage.getItem('refreshToken');

        if (!storedRefresh) {
            _redirectToLogin();
            return $.Deferred().reject('no_refresh_token').promise();
        }

        // If already refreshing, queue and return the queued promise
        if (_isRefreshing) {
            var deferred = $.Deferred();
            _refreshQueue.push(deferred);
            return deferred.promise();
        }

        // Skip if page is already unloading
        if (_isUnloading) {
            return $.Deferred().reject('unloading').promise();
        }

        _isRefreshing = true;

        var xhr = $.ajax({
            url: _apiBase() + REFRESH_PATH,
            method: 'POST',
            contentType: 'application/json',
            data: JSON.stringify({ refreshToken: storedRefresh })
        });

        // Track the refresh XHR so it gets aborted on page unload
        _pendingXhrs.add(xhr);
        xhr.always(function () {
            _pendingXhrs.delete(xhr);
        });

        return xhr.then(
            function (res) {
                if (res.success && res.data) {
                    setTokens(res.data.accessToken, res.data.refreshToken, res.data.expiresIn);
                    _resolveQueue();
                    _isRefreshing = false;
                } else {
                    _handleRefreshFailure();
                }
            },
            function (jqXhr) {
                // Suppress refresh failure during page unload
                if (_isUnloading) {
                    _isRefreshing = false;
                    _rejectQueue();
                    return;
                }
                // On 429 Too Many Requests � DON'T wipe tokens, just reject.
                // The tokens are still valid; we're just rate-limited.
                if (jqXhr && jqXhr.status === 429) {
                    _isRefreshing = false;
                    _rejectQueue();
                    return;
                }
                _handleRefreshFailure();
            }
        );
    }

    function _resolveQueue() {
        for (var i = 0; i < _refreshQueue.length; i++) {
            _refreshQueue[i].resolve();
        }
        _refreshQueue = [];
    }

    function _rejectQueue() {
        for (var i = 0; i < _refreshQueue.length; i++) {
            _refreshQueue[i].reject();
        }
        _refreshQueue = [];
    }

    function _handleRefreshFailure() {
        _isRefreshing = false;
        _rejectQueue();
        clearTokens();
        _redirectToLogin();
    }

    // ??????????????????????????????????????
    // PUBLIC: clearTokens
    // Clears everything: tokens, me cache, session cookie, CF.user.
    // ??????????????????????????????????????
    function clearTokens() {
        _accessToken = null;
        _expiresAt   = null;
        _clearPersistedTokens();
        sessionStorage.removeItem('refreshToken');
        sessionStorage.removeItem('accessToken');
        sessionStorage.removeItem('expiresIn');
        _clearMeCache();
        _clearSessionCookie();
        CF.user._clear();

        // Reset notification bell state
        if (typeof CF.notification !== 'undefined' && CF.notification.reset) {
            CF.notification.reset();
        }
    }

    // ??????????????????????????????????????
    // PUBLIC: logout
    // ??????????????????????????????????????
    function logout() {
        if (typeof CF.signalR !== 'undefined' && CF.signalR.stop) {
            CF.signalR.stop();
        }
        clearTokens();
        _redirectToLogin();
    }

    function _redirectToLogin() {
        if (window.location.pathname !== LOGIN_PATH) {
            window.location.href = LOGIN_PATH;
        }
    }

    // ??????????????????????????????????????
    // PUBLIC: api(options)
    // Drop-in replacement for $.ajax that:
    //   1. Proactively refreshes if token expiring within 60s
    //   2. Attaches Authorization: Bearer header automatically
    //   3. On 401 ? refreshes once ? retries original request
    //   4. On refresh failure ? redirects to /login
    //
    // Usage:
    //   CF.auth.api({ url: '/api/v1/users/list', method: 'GET' })
    //     .then(function(res) { ... })
    //     .fail(function(xhr) { ... });
    // ??????????????????????????????????????
    function api(options) {
        var opts = $.extend(true, {}, options);

        // Skip API calls if page is already unloading
        if (_isUnloading) {
            return $.Deferred().reject().promise();
        }

        // Ensure full URL
        if (opts.url && opts.url.indexOf('http') !== 0) {
            opts.url = _apiBase() + opts.url;
        }

        function _attachHeader(o) {
            o.headers = o.headers || {};
            if (_accessToken) {
                o.headers['Authorization'] = 'Bearer ' + _accessToken;
            }
        }

        function _trackXhr(xhr) {
            if (xhr && typeof xhr.abort === 'function') {
                _pendingXhrs.add(xhr);
                xhr.always(function () {
                    _pendingXhrs.delete(xhr);
                });
            }
            return xhr;
        }

        function _doRequest(o) {
            _attachHeader(o);
            var xhr = $.ajax(o);
            _trackXhr(xhr);
            return xhr.then(null, function (jqXhr) {
                // Suppress errors from aborted requests during navigation
                if (_isUnloading || jqXhr.statusText === 'abort') {
                    return $.Deferred().reject(jqXhr).promise();
                }
                // On 401: try refresh once, then retry
                if (jqXhr.status === 401) {
                    return _refreshTokens().then(function () {
                        var retryOpts = $.extend(true, {}, o);
                        _attachHeader(retryOpts);
                        var retryXhr = $.ajax(retryOpts);
                        _trackXhr(retryXhr);
                        return retryXhr;
                    });
                }
                return $.Deferred().reject(jqXhr).promise();
            });
        }

        // Proactive refresh if token is expiring soon
        if (_isTokenExpiringSoon() && sessionStorage.getItem('refreshToken')) {
            return _refreshTokens().then(function () {
                return _doRequest(opts);
            });
        }

        return _doRequest(opts);
    }

    // ??????????????????????????????????????
    // PUBLIC: loadMe(forceRefresh)
    // Populates CF.user and renders sidebar + header profile.
    //
    // Strategy:
    //   1. If forceRefresh=false and cf_me exists in sessionStorage
    //      ? use cached data (zero API calls � instant render)
    //   2. Otherwise ? call GET /api/v1/auth/me ? cache result
    //   3. If avatarFileId present and avatarDataUrl not cached
    //      ? fetch thumbnail once ? convert to base64 ? update cache
    //
    // Cache invalidated by:
    //   - CF.auth.logout()             ? clearTokens() removes cf_me
    //   - CF.auth.invalidateMe()       ? page scripts call after changes
    //   - CF.auth.redirectAfterLogin() ? clears stale cache on new login
    //   - SignalR "user.context-changed" ? cf-signalr.js calls invalidateMe(true)
    //
    // Returns a jQuery promise (resolved immediately if cached).
    // ??????????????????????????????????????
    function loadMe(forceRefresh) {
        // Try sessionStorage cache first
        if (!forceRefresh) {
            var cached = _getMeCache();
            if (cached) {
                _applyMeData(cached);
                return $.Deferred().resolve().promise();
            }
        }

        // Cache miss or forced � call API
        return api({ url: ME_PATH, method: 'GET' }).then(
            function (res) {
                if (res.success && res.data) {
                    _setMeCache(res.data);
                    _applyMeData(res.data);
                }
            },
            function () {
                // /me failed � api() already handles 401 ? refresh ? retry
            }
        );
    }

    // ??????????????????????????????????????
    // PUBLIC: invalidateMe(reload)
    // Clears cached MeResponse so the next loadMe() hits the API.
    // Call this from page scripts after admin changes role/menu/permission,
    // or user updates own profile / avatar.
    //
    // If reload=true, immediately fetches fresh data and re-renders UI.
    // ??????????????????????????????????????
    function invalidateMe(reload) {
        _clearMeCache();
        if (reload) {
            return loadMe(true);
        }
        return $.Deferred().resolve().promise();
    }

    // ??????????????????????????????????????
    // INTERNAL: apply MeResponse to CF.user + render UI
    // Shared between cache-hit and API-fetch paths.
    // ??????????????????????????????????????
    function _applyMeData(me) {
        CF.user._load(me);

        // Page-level permission guard.
        // window.PAGE_PERMISSION is set in _Layout.cshtml from
        // ViewData["RequiredPermission"] populated by [PagePermission(...)] attribute.
        // If user lacks the permission, redirect to /access-denied before rendering.
        var requiredPerm = window.PAGE_PERMISSION;
        if (requiredPerm && !CF.user.hasPermission(requiredPerm)) {
            window.location.replace('/access-denied');
            return;
        }

        // Reveal main content (was hidden via .cf-pre-auth to prevent flash).
        var mainEl = document.querySelector('.main-content');
        if (mainEl) mainEl.classList.remove('cf-pre-auth');

        // Apply [data-cf-permission] guards to any elements already in the DOM.
        if (typeof CF.permission !== 'undefined' && CF.permission.applyGuards) {
            CF.permission.applyGuards(document);
        }

        if (typeof CF.appConfig !== 'undefined' && CF.appConfig.load) {
            CF.appConfig.load(me);
        }

        if (typeof window.renderSidebarFromMe === 'function') {
            window.renderSidebarFromMe(me.menus);
        }

        _renderHeaderProfile(me);
        _loadAvatarThumbnail(me);

        // Notify other modules that CF.user is populated and permissions are ready.
        // Used by datatable.js to (re)evaluate primaryAction/actions permission filters.
        try {
            document.dispatchEvent(new CustomEvent('cf:user-loaded'));
        } catch (e) {
            // IE/old browsers fallback � not expected in this app
        }

        // Start SignalR connection (idempotent � skips if already connected)
        if (typeof CF.signalR !== 'undefined' && CF.signalR.start) {
            CF.signalR.start();
        }

        // Load notification bell data (idempotent � skips if already initialized)
        if (typeof CF.notification !== 'undefined' && CF.notification.init) {
            CF.notification.init();
        }
    }

    // ??????????????????????????????????????
    // INTERNAL: render header profile from MeResponse
    // ??????????????????????????????????????
    function _renderHeaderProfile(me) {
        var initials = _getInitials(me.fullName || me.username);

        // Header avatar � show image if cached, otherwise initials
        var avatarEl = document.querySelector('.user-avatar');
        if (avatarEl) {
            if (me.avatarDataUrl) {
                avatarEl.innerHTML = '<img src="' + me.avatarDataUrl + '" alt="Avatar" style="width:100%;height:100%;border-radius:50%;object-fit:cover" />';
            } else {
                avatarEl.innerHTML = '<span>' + initials + '</span>';
            }
        }

        var el;
        el = document.querySelector('.user-name');
        if (el) el.textContent = me.fullName || me.username;

        el = document.querySelector('.user-role');
        if (el) el.textContent = (me.roles && me.roles.length > 0) ? me.roles[0] : '';

        el = document.querySelector('.user-dd-name');
        if (el) el.textContent = me.fullName || me.username;

        el = document.querySelector('.user-dd-email');
        if (el) el.textContent = me.email;

        _renderCompanySwitcher(me);
    }

    // ------------------------------------------------------------
    // INTERNAL: switcher company aktif (multi-company script 093).
    // Tampil hanya bila user di-mapping ke >1 perusahaan; pilih company
    // -> POST /auth/switch-company -> cache me dibuang -> reload halaman
    // (seluruh daftar ber-scope company aktif dimuat ulang).
    // ------------------------------------------------------------
    function _renderCompanySwitcher(me) {
        var companies = me.companies || [];

        var active = null;
        for (var i = 0; i < companies.length; i++) {
            if (companies[i].id === me.activeCompanyId) { active = companies[i]; break; }
        }
        if (!active) active = companies[0];

        // Subjudul brand sidebar mengikuti company aktif:
        // "PT Aruna Distribusi Nusantara" -> "Distribusi Nusantara"
        var brandEl = document.getElementById('sidebar-brand-company');
        if (brandEl && active) {
            brandEl.textContent = active.name
                .replace(/^PT\s+/i, '')
                .replace(/^Aruna\s+/i, '');
        }

        var wrap = document.getElementById('company-switcher');
        if (!wrap) return;
        if (companies.length < 2) { wrap.hidden = true; return; }

        var esc = function (v) {
            return String(v == null ? '' : v)
                .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;');
        };

        var codeEl = document.getElementById('company-active-code');
        if (codeEl) codeEl.textContent = active.code;
        var btn = document.getElementById('company-switcher-btn');
        if (btn) btn.title = active.name;

        var list = document.getElementById('company-dd-list');
        if (list) {
            var html = '';
            for (var j = 0; j < companies.length; j++) {
                var c = companies[j];
                var isActive = c.id === active.id;
                html += '<a href="javascript:void(0)" class="user-dd-item"' +
                    (isActive ? ' style="font-weight:600;color:#2563EB"' : '') +
                    ' data-company-id="' + c.id + '"' +
                    ' onclick="CF.auth.switchCompany(this.dataset.companyId)">' +
                    '<i class="fa-solid ' + (isActive ? 'fa-circle-check' : 'fa-building') + '"></i>' +
                    esc(c.code + ' — ' + c.name) + '</a>';
            }
            list.innerHTML = html;
        }

        wrap.hidden = false;
    }

    // ------------------------------------------------------------
    // PUBLIC: switchCompany(companyId)
    // Pindah company aktif lalu muat ulang halaman.
    // ------------------------------------------------------------
    function switchCompany(companyId) {
        return api({
            url: '/api/v1/auth/switch-company',
            method: 'POST',
            contentType: 'application/json',
            data: JSON.stringify({ companyId: companyId })
        }).then(function (res) {
            if (res && res.success) {
                _clearMeCache();
                window.location.reload();
            } else if (typeof CF !== 'undefined' && CF.toast) {
                CF.toast.error((res && res.message) || 'Gagal mengganti perusahaan.');
            }
        });
    }

    // ??????????????????????????????????????
    // INTERNAL: load avatar thumbnail and cache as Base64 Data URL
    //
    // Fetches /api/v1/files/{avatarFileId}/thumbnail once, converts the
    // binary response to a base64 Data URL, stores it in the cf_me
    // sessionStorage cache. Subsequent page navigations render the
    // avatar instantly from cache � zero additional API calls.
    //
    // Thumbnail is ~200px max, ~5-15KB as JPEG � well within
    // sessionStorage limits (~5MB).
    // ??????????????????????????????????????
    function _loadAvatarThumbnail(me) {
        // Already cached as Data URL � nothing to do
        if (me.avatarDataUrl) return;

        // No avatar � nothing to fetch
        if (!me.avatarFileId) return;

        // Skip if page is unloading
        if (_isUnloading) return;

        // Fetch thumbnail as blob using native fetch (supports auth header + blob response)
        var thumbnailUrl = _apiBase() + '/api/v1/files/' + me.avatarFileId + '/thumbnail';

        var headers = {};
        if (_accessToken) {
            headers['Authorization'] = 'Bearer ' + _accessToken;
        }

        fetch(thumbnailUrl, { headers: headers })
            .then(function (response) {
                if (!response.ok) return null;
                return response.blob();
            })
            .then(function (blob) {
                if (!blob) return;

                var reader = new FileReader();
                reader.onloadend = function () {
                    var dataUrl = reader.result;

                    // Update in-memory user context
                    CF.user.avatarDataUrl = dataUrl;

                    // Update sessionStorage cache so next page load is instant
                    _updateMeCacheAvatar(dataUrl);

                    // Render the avatar image in header
                    var avatarEl = document.querySelector('.user-avatar');
                    if (avatarEl) {
                        avatarEl.innerHTML = '<img src="' + dataUrl + '" alt="Avatar" style="width:100%;height:100%;border-radius:50%;object-fit:cover" />';
                    }
                };
                reader.readAsDataURL(blob);
            })
            .catch(function () {
                // Silently ignore � initials fallback already shown
            });
    }

    function _getInitials(name) {
        if (!name) return '??';
        var parts = name.trim().split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
        }
        return parts[0].substring(0, 2).toUpperCase();
    }

    // ??????????????????????????????????????
    // INIT � runs on every non-login page load.
    // Restores accessToken from sessionStorage (survives navigation),
    // only calls refresh-token if token is missing or expired.
    //
    // loadMe() will use sessionStorage cache if available,
    // so subsequent page navigations are instant (zero API calls).
    // ??????????????????????????????????????
    function _init() {
        if (window.location.pathname === LOGIN_PATH) return;

        // Migrate legacy accessToken from sessionStorage (old login.js format)
        var legacyAccess  = sessionStorage.getItem('accessToken');
        var legacyExpires = sessionStorage.getItem('expiresIn');

        if (legacyAccess) {
            _accessToken = legacyAccess;
            if (legacyExpires) {
                _expiresAt = Date.now() + (parseInt(legacyExpires, 10) * 1000);
            }
            sessionStorage.removeItem('accessToken');
            sessionStorage.removeItem('expiresIn');
            _persistTokens();
            _setSessionCookie();
            loadMe();
            return;
        }

        // Try to restore accessToken from sessionStorage (persisted on previous page)
        var restored = _restoreTokens();

        if (restored && !_isTokenExpiringSoon()) {
            // Token still valid and not expiring soon � zero API calls
            if (!_hasSessionCookie()) {
                _setSessionCookie();
            }
            loadMe();
            return;
        }

        // Token missing or expiring soon � need to refresh
        var storedRefresh = sessionStorage.getItem('refreshToken');

        if (storedRefresh) {
            if (!_hasSessionCookie()) {
                _setSessionCookie();
            }
            _refreshTokens().then(function () {
                loadMe();
            });
        } else {
            // No refreshToken � session is gone, redirect to login.
            _redirectToLogin();
        }
    }

    $(document).ready(function () {
        _init();
    });

    // ??????????????????????????????????????
    // PUBLIC API
    // ??????????????????????????????????????
    return {
        setTokens:          setTokens,
        redirectAfterLogin: redirectAfterLogin,
        getAccessToken:     getAccessToken,
        isAuthenticated:    isAuthenticated,
        clearTokens:        clearTokens,
        logout:             logout,
        api:                api,
        loadMe:             loadMe,
        invalidateMe:       invalidateMe,
        switchCompany:      switchCompany
    };

})();
